/**
 * 06 · El año de un vistazo: desvío vs BGT, mes × línea. Responde lo que el
 * cierre mensual no puede: ¿es de este mes o viene pasando? Escala divergente
 * con gris en el medio: el cero se lee como "acá no pasó nada".
 */

import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { computeHeatmap, type PnlHeatmapRow } from '../../../../../shared/finance-pnl';
import type { PnlDashboardData } from '../../../../../shared/types/finance-pnl';
import { MINUS, type PnlFmt } from './pnl-format';
import { PnlNote, SectionHead } from './pnl-ui';

const ROW_KEY: Record<PnlHeatmapRow['key'], string> = {
  revenue: 'budget_control.lines.revenue',
  cogs: 'budget_control.lines.cogs',
  gya: 'budget_control.lines.gya',
  investment: 'budget_control.lines.investment',
  bonos: 'budget_control.lines.bonos',
  taxes: 'budget_control.lines.taxes',
  ebitdaPct: 'budget_control.heatmap.ebitda_pp',
};

const SCALE = ['n3', 'n2', 'n1', 'n0', 'z', 'p0', 'p1', 'p2', 'p3'] as const;

function tone(v: number): string {
  const a = Math.abs(v);
  if (a < 1) return 'z';
  if (v < 0) return a < 5 ? 'n0' : a < 12 ? 'n1' : a < 22 ? 'n2' : 'n3';
  return a < 5 ? 'p0' : a < 12 ? 'p1' : a < 22 ? 'p2' : 'p3';
}

export function PnlHeatmap({ data, fmt, notes }: { data: PnlDashboardData; fmt: PnlFmt; notes: boolean }) {
  const { t } = useTranslation();
  const rows = computeHeatmap(data);
  const months = Array.from({ length: data.closedMonth }, (_, i) => i + 1);
  return (
    <section>
      <SectionHead num="07" title={t('budget_control.heatmap.title')} sub={t('budget_control.heatmap.subtitle')} />
      <Card className="p-4 shadow-xs">
        <div className="overflow-x-auto">
          <div className="grid min-w-[660px] gap-[3px]" style={{ gridTemplateColumns: `150px repeat(${months.length}, minmax(44px, 1fr))` }}>
            <div />
            {months.map((m) => (
              <div key={m} className="pb-0.5 text-center text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                {fmt.monthShort(m)}
              </div>
            ))}
            {rows.map((r) => (
              <div key={r.key} className="contents">
                <div className="flex items-center justify-end pr-2 text-right text-xs text-muted-foreground">{t(ROW_KEY[r.key])}</div>
                {r.cells.map((v, i) => {
                  const tn = v === null ? 'z' : tone(v);
                  const a = v === null ? 0 : Math.abs(v);
                  return (
                    <div
                      key={i}
                      className="grid h-[34px] place-items-center rounded font-mono text-[11px] font-semibold tabular-nums"
                      style={{
                        background: `var(--pnl-hm-${tn})`,
                        color: a >= 12 ? '#fff' : a < 1 ? 'var(--pnl-ink-3)' : 'var(--pnl-ink)',
                      }}
                      title={`${t(ROW_KEY[r.key])} · ${fmt.monthShort(months[i])} · ${v === null ? '–' : r.key === 'ebitdaPct' ? fmt.pp(v) : fmt.sgnPct(v)}`}
                    >
                      {v === null ? '–' : `${v > 0 ? '+' : v < 0 ? MINUS : ''}${Math.abs(v).toFixed(0)}`}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          <span>{t('budget_control.heatmap.scale')}</span>
          {SCALE.map((s) => (
            <i key={s} className="block h-[11px] w-[26px] rounded-[2px]" style={{ background: `var(--pnl-hm-${s})` }} />
          ))}
          <span className="ml-1">{t('budget_control.heatmap.scale_hint')}</span>
        </div>
      </Card>
      <PnlNote
        show={notes}
        items={[
          { tag: 'new', title: t('budget_control.notes.heatmap.new_title'), text: t('budget_control.notes.heatmap.new_text') },
          { tag: 'color', title: t('budget_control.notes.heatmap.color_title'), text: t('budget_control.notes.heatmap.color_text') },
        ]}
      />
    </section>
  );
}
